import styles from "./NapAuditPanel.module.scss";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";
import { MessageBanner } from "../../../common/components/common/MessageBanner/MessageBanner";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";
import { useNapAuditPanel } from "./hooks/useNapAuditPanel";
import { NapXsdValidationStatus } from "../../../common/enums";

const MONTH_NAMES = [
  "Януари",
  "Февруари",
  "Март",
  "Април",
  "Май",
  "Юни",
  "Юли",
  "Август",
  "Септември",
  "Октомври",
  "Ноември",
  "Декември",
];

const formatAmount = (value: number, currency: string | null): string =>
  `${value.toFixed(2)} ${(currency || "").toUpperCase()}`.trim();

const formatDate = (value: string): string =>
  value ? new Date(value).toLocaleDateString("bg-BG") : "";

export const NapAuditPanel = () => {
  const {
    year,
    month,
    setYear,
    setMonth,
    yearOptions,
    rows,
    docWarnings,
    isLoading,
    load,
    updateRow,
    isSaving,
    result,
    isGenerating,
    generate,
    download,
  } = useNapAuditPanel();

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-базиран

  // Недостъпни са само бъдещите месеци (текущият и миналите са позволени).
  const isPeriodDisabled = (y: number, m: number): boolean =>
    y > currentYear || (y === currentYear && m > currentMonth);

  const monthOptions = MONTH_NAMES.map((name, index) => ({
    label: name,
    value: index + 1,
    disabled: isPeriodDisabled(year, index + 1),
  }));

  const yearDropdownOptions = yearOptions.map((y) => ({
    label: String(y),
    value: y,
  }));

  // При смяна на година, ако избраният месец стане бъдещ — приравни към последния валиден.
  const onYearChange = (y: number) => {
    if (isPeriodDisabled(y, month)) {
      setMonth(y < currentYear ? 12 : currentMonth);
    }
    setYear(y);
  };

  return (
    <div className={styles.root}>
      <MessageBanner
        type="info"
        message="Генерира се стандартизираният одиторски файл (НАП - Приложение №38) за неприсъствените картови плащания (Stripe) на всички обекти за избран минал месец. Моля, имайте предвид че всички полета в секция 'Stripe' в настройките на профила на собственика трябва да са правилно попълнени спрямо заявлението за регистриране на онлайн магазин, подадено към НАП."
      />

      <div className={styles.root_period}>
        <label className={styles.root_field}>
          <span>Месец</span>
          <Dropdown
            value={month}
            options={monthOptions}
            optionDisabled="disabled"
            onChange={(e) => setMonth(e.value)}
            style={{ minWidth: "160px" }}
          />
        </label>

        <label className={styles.root_field}>
          <span>Година</span>
          <Dropdown
            value={year}
            options={yearDropdownOptions}
            onChange={(e) => onYearChange(e.value)}
            style={{ minWidth: "160px" }}
          />
        </label>

        <DefaultButton
          label="Зареди"
          type="button"
          onClick={load}
          loading={isLoading}
        />
      </div>

      {docWarnings.map((warning, index) => (
        <MessageBanner key={index} type="warning" message={warning} />
      ))}

      <RenderWhen condition={rows !== null}>
        <RenderWhen condition={(rows?.length ?? 0) === 0}>
          <MessageBanner
            type="info"
            message="Няма доставени картови (Stripe) плащания за избрания период."
          />
        </RenderWhen>

        <RenderWhen condition={(rows?.length ?? 0) > 0}>
          <div className={styles.root_hint}>
            Редактирайте номер, име на продукт и дата на документа. Изключените редове
            не влизат във файла, а номерата се преномерират автоматично като непрекъсната
            последователност.
          </div>

          <div className={styles.root_cards}>
            {rows?.map((row, index) => (
              <div
                key={row.documentId}
                className={`${styles.card} ${row.isExcluded ? styles.card_excluded : ""}`}
              >
                <div className={styles.card_head}>
                  <span className={styles.card_head_meta}>
                    Резервация #{row.reservationId} · платено {formatDate(row.paidAt)}
                  </span>
                  <label className={styles.card_head_exclude}>
                    <span>Изключи от файла</span>
                    <InputSwitch
                      checked={row.isExcluded}
                      onChange={(e) =>
                        updateRow(index, { isExcluded: !!e.value })
                      }
                    />
                  </label>
                </div>

                <div className={styles.card_fields}>
                  <label className={styles.card_field}>
                    <span>Номер</span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={row.isExcluded ? "" : row.number}
                      disabled={row.isExcluded}
                      onChange={(e) =>
                        updateRow(index, {
                          number: e.target.value === "" ? 0 : Number(e.target.value),
                        })
                      }
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    />
                  </label>

                  <label className={`${styles.card_field} ${styles.card_field_wide}`}>
                    <span>Име на продукт</span>
                    <input
                      type="text"
                      value={row.productName ?? ""}
                      disabled={row.isExcluded}
                      placeholder={row.eventName}
                      onChange={(e) =>
                        updateRow(index, { productName: e.target.value })
                      }
                    />
                  </label>

                  <label className={styles.card_field}>
                    <span>Дата на документа</span>
                    <Calendar
                      value={row.issueDate ? new Date(row.issueDate) : null}
                      onChange={(e) =>
                        updateRow(index, {
                          issueDate: e.value
                            ? (e.value as Date).toISOString()
                            : row.issueDate,
                        })
                      }
                      dateFormat="dd/mm/yy"
                      showIcon
                      disabled={row.isExcluded}
                    />
                  </label>
                </div>

                <div className={styles.card_amounts}>
                  <span>
                    Нето: <strong>{formatAmount(row.netAmount, row.currency)}</strong>
                  </span>
                  <span>
                    ДДС: <strong>{formatAmount(row.vatAmount, row.currency)}</strong>
                  </span>
                  <span>
                    Бруто: <strong>{formatAmount(row.grossAmount, row.currency)}</strong>
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.root_actions}>
            <DefaultButton
              label="Генерирай файл"
              type="button"
              onClick={generate}
              loading={isGenerating || isSaving}
            />
          </div>
        </RenderWhen>
      </RenderWhen>

      <RenderWhen condition={!!result}>
        {result && (
          <div className={styles.root_preview}>
            <div className={styles.root_preview_summary}>
              <div className={styles.root_preview_summary_item}>
                <span>Поръчки</span>
                <strong>{result.orderCount}</strong>
              </div>
              <div className={styles.root_preview_summary_item}>
                <span>Обща сума</span>
                <strong>
                  {formatAmount(result.totalGross, result.currency)}
                </strong>
              </div>
              <div className={styles.root_preview_summary_item}>
                <span>Нето</span>
                <strong>{formatAmount(result.totalNet, result.currency)}</strong>
              </div>
              <div className={styles.root_preview_summary_item}>
                <span>ДДС</span>
                <strong>{formatAmount(result.totalVat, result.currency)}</strong>
              </div>
            </div>

            {result.warnings.map((warning, index) => (
              <MessageBanner key={index} type="warning" message={warning} />
            ))}

            <RenderWhen
              condition={
                result.orderCount > 0 &&
                result.validationStatus === NapXsdValidationStatus.Valid
              }
            >
              <MessageBanner
                type="info"
                message="Изтеглянето ще започне всеки момент."
              />
            </RenderWhen>

            <RenderWhen
              condition={
                result.orderCount > 0 &&
                result.validationStatus === NapXsdValidationStatus.SchemaMissing
              }
            >
              <MessageBanner
                type="warning"
                message="Официалната XSD схема на НАП не е инсталирана, затова файлът не е валидиран и не може да се свали. Добавете .xsd файла в Services/Nap/Schemas."
              />
            </RenderWhen>

            <RenderWhen
              condition={
                result.orderCount > 0 &&
                result.validationStatus === NapXsdValidationStatus.Invalid
              }
            >
              <MessageBanner
                type="error"
                message="Файлът НЕ е валиден спрямо XSD схемата и не може да се свали:"
              />
              <ul className={styles.root_preview_errors}>
                {result.validationErrors.slice(0, 20).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </RenderWhen>

            <div className={styles.root_preview_actions}>
              <RenderWhen condition={result.canDownload}>
                <DefaultButton
                  label="Свали XML"
                  type="button"
                  onClick={download}
                />
              </RenderWhen>
            </div>
          </div>
        )}
      </RenderWhen>
    </div>
  );
};
